import {
  EmailAuthProvider,
  updateEmail as firebaseUpdateEmail,
  updatePassword as firebaseUpdatePassword,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
  OAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  type User,
  unlink,
  updateProfile
} from 'firebase/auth'
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { auth, storage } from './config'
import { callRequestAccountDeletion } from './functions'

export async function updateDisplayName(name: string): Promise<void> {
  const user = auth.currentUser
  if (!user) throw new Error('No authenticated user')
  await updateProfile(user, { displayName: name })
}

export async function updateEmail(newEmail: string, currentPassword: string): Promise<void> {
  const user = auth.currentUser
  if (!user || !user.email) throw new Error('No authenticated user')
  const credential = EmailAuthProvider.credential(user.email, currentPassword)
  await reauthenticateWithCredential(user, credential)
  await firebaseUpdateEmail(user, newEmail)
}

export async function updatePassword(currentPassword: string, newPassword: string): Promise<void> {
  const user = auth.currentUser
  if (!user || !user.email) throw new Error('No authenticated user')
  const credential = EmailAuthProvider.credential(user.email, currentPassword)
  await reauthenticateWithCredential(user, credential)
  await firebaseUpdatePassword(user, newPassword)
}

export async function addPassword(newPassword: string): Promise<void> {
  const user = auth.currentUser
  if (!user || !user.email) throw new Error('No authenticated user')
  const credential = EmailAuthProvider.credential(user.email, newPassword)
  await linkWithCredential(user, credential)
}

export async function linkGoogle(): Promise<void> {
  const user = auth.currentUser
  if (!user) throw new Error('No authenticated user')
  await linkWithPopup(user, new GoogleAuthProvider())
}

export async function linkApple(): Promise<void> {
  const user = auth.currentUser
  if (!user) throw new Error('No authenticated user')
  await linkWithPopup(user, new OAuthProvider('apple.com'))
}

export async function unlinkProvider(providerId: string): Promise<void> {
  const user = auth.currentUser
  if (!user) throw new Error('No authenticated user')
  await unlink(user, providerId)
}

export async function uploadProfilePhoto(blob: Blob): Promise<void> {
  const user = auth.currentUser
  if (!user) throw new Error('No authenticated user')
  const photoRef = ref(storage, `profile-images/${user.uid}/avatar.webp`)
  await uploadBytes(photoRef, blob, { contentType: 'image/webp' })
  const downloadURL = await getDownloadURL(photoRef)
  await updateProfile(user, { photoURL: downloadURL })
  await user.reload()
}

export async function removeProfilePhoto(): Promise<void> {
  const user = auth.currentUser
  if (!user) throw new Error('No authenticated user')
  await updateProfile(user, { photoURL: '' })
  await user.reload()
  try {
    await deleteObject(ref(storage, `profile-images/${user.uid}/avatar.webp`))
  } catch {
    // File may not exist if the photo was set externally (e.g. Google sign-in) — not an error
  }
}

/** How an account proves who it is, from the providers it has linked. */
export enum ReauthMethod {
  Password = 'password',
  Google = 'google',
  Apple = 'apple'
}

const PROVIDER_ID: Record<ReauthMethod, string> = {
  [ReauthMethod.Password]: 'password',
  [ReauthMethod.Google]: 'google.com',
  [ReauthMethod.Apple]: 'apple.com'
}

/**
 * Which method to ask for. Password wins whenever one is linked — it needs no popup, so it can't be
 * blocked, and an account with several providers only has to remember one thing.
 */
export function reauthMethodFor(user: User): ReauthMethod {
  const linked = new Set(user.providerData.map((p) => p.providerId))
  if (linked.has(PROVIDER_ID[ReauthMethod.Password])) return ReauthMethod.Password
  if (linked.has(PROVIDER_ID[ReauthMethod.Apple]) && !linked.has(PROVIDER_ID[ReauthMethod.Google])) return ReauthMethod.Apple
  return ReauthMethod.Google
}

/**
 * Prove the person at the keyboard is the account holder, immediately before something irreversible.
 * Also refreshes the token's sign-in time, which is what stops `deleteUser` demanding it separately.
 *
 * `password` is read only for {@link ReauthMethod.Password}; the popup methods ignore it.
 */
export async function reauthenticate(method: ReauthMethod, password: string): Promise<void> {
  const user = auth.currentUser
  if (!user) throw new Error('No authenticated user')

  if (method === ReauthMethod.Password) {
    if (!user.email) throw new Error('No email on the authenticated user')
    await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password))
    return
  }

  const provider = method === ReauthMethod.Google ? new GoogleAuthProvider() : new OAuthProvider(PROVIDER_ID[ReauthMethod.Apple])
  await reauthenticateWithPopup(user, provider)
}

/**
 * Ask for the signed-in user's account to be deleted.
 *
 * One call, and the server owns the rest. Cancelling the subscription and deleting the auth user are
 * two writes to two systems with no transaction between them: sequencing them from here meant a
 * dropped connection could leave a terminally cancelled subscription on a live account, with nothing
 * to notice or recover it.
 *
 * Returns the one-time token for answering the offboarding survey, which is asked after sign-out.
 */
export async function deleteAccount(): Promise<string | null> {
  if (!auth.currentUser) throw new Error('No authenticated user')
  const { data } = await callRequestAccountDeletion()
  return data.feedbackToken
}
